/**
 * What a "Go to" scan shows: the record, where it can be opened, or why nothing was found.
 *
 * It never navigates by itself. Where a record opens comes from the app's destination resolver
 * (`HeadlightQR` option `DestinationResolver`), so the same sheet works in every app.
 */

const libPictView = require('pict-view');
const libMatrix = require('../rendering/QR-Matrix.js');

const SHEET_ID = 'HeadlightQR-ResultSheet';

const _DefaultConfiguration =
{
	ViewIdentifier: 'HeadlightQR-ResultSheet',
	AutoRender: false,
	CSS: /*css*/`
.hlqr-sheet-backdrop { position: fixed; inset: 0; z-index: 1150; display: flex; align-items: flex-end; justify-content: center; background: rgba(15, 23, 42, 0.45); }
.hlqr-sheet { width: min(100vw, 560px); max-height: 85vh; overflow: auto; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2933); border-radius: 12px 12px 0 0; padding: 1.1rem 1.25rem 1.25rem; box-shadow: 0 -8px 30px rgba(0,0,0,0.3); }
@media (min-width: 700px) { .hlqr-sheet-backdrop { align-items: center; } .hlqr-sheet { border-radius: 12px; } }
.hlqr-sheet-kicker { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7; }
.hlqr-sheet-title { font-size: 1.25rem; font-weight: 700; margin: 0.15rem 0 0.5rem; word-break: break-word; }
.hlqr-sheet-message { margin: 0.25rem 0 0.75rem; line-height: 1.4; }
.hlqr-sheet-fields { display: grid; grid-template-columns: max-content 1fr; gap: 0.25rem 0.9rem; margin: 0.5rem 0 1rem; font-size: 0.9rem; }
.hlqr-sheet-fields dt { opacity: 0.7; }
.hlqr-sheet-fields dd { margin: 0; word-break: break-word; }
.hlqr-sheet-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.hlqr-sheet-button { border: 1px solid var(--theme-color-border-default, #c5ccd3); background: transparent; color: inherit; border-radius: 6px; padding: 0.45rem 0.9rem; cursor: pointer; font-size: 0.9rem; }
.hlqr-sheet-button--primary { background: #1d4ed8; border-color: #1d4ed8; color: #fff; font-weight: 600; }
.hlqr-sheet-pill { display: inline-block; font-size: 0.75rem; padding: 0.1rem 0.5rem; border-radius: 999px; background: rgba(0,0,0,0.08); margin-left: 0.4rem; vertical-align: middle; }
.hlqr-sheet-pill--deleted { background: #b91c1c; color: #fff; }
`,
};

/**
 * @param {any} pValue
 * @return {string}
 */
function escape(pValue)
{
	return libMatrix.escapeXML(pValue === null || pValue === undefined ? '' : String(pValue));
}

/**
 * @param {string} pISO
 * @return {string}
 */
function formatDate(pISO)
{
	const tmpDate = new Date(pISO);
	if (!pISO || isNaN(tmpDate.getTime()))
	{
		return 'an unknown date';
	}
	return tmpDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

class HeadlightQRResultSheet extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultConfiguration, pOptions), pServiceHash);
		/** @type {any} */
		this.pict;
		/** @type {Array<any>} */
		this.currentDestinations = [];
	}

	/**
	 * @param {import('../providers/Pict-Provider-HeadlightQR.js').QRResolution} pResolution
	 * @return {Promise<void>}
	 */
	async show(pResolution)
	{
		const tmpProvider = this.pict.providers.HeadlightQR;
		const tmpDefinition = pResolution.Entity ? tmpProvider.getEntityDefinition(pResolution.Entity) : null;
		const tmpNoun = tmpDefinition ? tmpDefinition.Label : 'code';
		let tmpBody = '';
		this.currentDestinations = [];

		switch (pResolution.Status)
		{
			case 'Found':
			{
				const tmpSummary = tmpProvider.describeRecord(pResolution.Entity, pResolution.Record);
				const tmpRouting = await tmpProvider.getDestinations(pResolution);
				this.currentDestinations = tmpRouting.Destinations || [];
				const tmpFields = (tmpSummary.Fields || []).map((pField) => `<dt>${escape(pField.Label)}</dt><dd>${escape(pField.Value)}</dd>`).join('');
				const tmpStatusPill = tmpSummary.Status ? `<span class="hlqr-sheet-pill">${escape(tmpSummary.Status)}</span>` : '';
				const tmpBlocked = tmpRouting.Blocked ? `<p class="hlqr-sheet-message">${escape(tmpRouting.Blocked.Message)}</p>` : '';
				const tmpButtons = this.currentDestinations.map((pDestination, pIndex) =>
					`<button type="button" class="hlqr-sheet-button${pIndex === 0 ? ' hlqr-sheet-button--primary' : ''}" data-hlqr-destination="${pIndex}">${escape(pDestination.Label)}</button>`).join('');
				tmpBody = `<div class="hlqr-sheet-kicker">${escape(tmpNoun)}</div>`
					+ `<div class="hlqr-sheet-title">${escape(tmpSummary.Title)}${tmpStatusPill}</div>`
					+ tmpBlocked
					+ (tmpFields ? `<dl class="hlqr-sheet-fields">${tmpFields}</dl>` : '')
					+ `<div class="hlqr-sheet-actions">${tmpButtons}<button type="button" class="hlqr-sheet-button" data-hlqr-close>Close</button></div>`;
				break;
			}
			case 'Deleted':
			{
				const tmpSummary = tmpProvider.describeRecord(pResolution.Entity, pResolution.Record);
				const tmpWho = pResolution.DeletedByName ? ` by ${escape(pResolution.DeletedByName)}` : '';
				tmpBody = `<div class="hlqr-sheet-kicker">${escape(tmpNoun)}</div>`
					+ `<div class="hlqr-sheet-title">${escape(tmpSummary.Title)}<span class="hlqr-sheet-pill hlqr-sheet-pill--deleted">Deleted</span></div>`
					+ `<p class="hlqr-sheet-message">This ${escape(tmpNoun)} was deleted on ${escape(formatDate(pResolution.Record.DeleteDate))}${tmpWho}. The label is still readable, but the record is no longer in use.</p>`
					+ `<div class="hlqr-sheet-actions"><button type="button" class="hlqr-sheet-button" data-hlqr-close>Close</button></div>`;
				break;
			}
			case 'Rejected':
			{
				const tmpMessage = (pResolution.Reason === 'ActionOutOfContext')
					? `That card is for the "${escape(pResolution.Decoded.Workflow)}" job. Start that job first, then scan the card again.`
					: `That's a ${escape(tmpNoun)}, which this screen doesn't take.`;
				tmpBody = `<div class="hlqr-sheet-title">Not accepted here</div><p class="hlqr-sheet-message">${tmpMessage}</p>`
					+ `<div class="hlqr-sheet-actions"><button type="button" class="hlqr-sheet-button" data-hlqr-close>Close</button></div>`;
				break;
			}
			case 'Unresolved':
			{
				tmpBody = `<div class="hlqr-sheet-title">Code read</div><p class="hlqr-sheet-message"><code>${escape(pResolution.Decoded.Value || pResolution.Decoded.Raw)}</code></p>`
					+ `<div class="hlqr-sheet-actions"><button type="button" class="hlqr-sheet-button" data-hlqr-close>Close</button></div>`;
				break;
			}
			default:
			{
				const tmpCode = (pResolution.Decoded && (pResolution.Decoded.Value || pResolution.Decoded.Raw)) || '';
				tmpBody = `<div class="hlqr-sheet-title">Nothing found</div>`
					+ `<p class="hlqr-sheet-message">We couldn't find anything with the code <code>${escape(tmpCode)}</code>. Check the label isn't damaged, or search for it by hand.</p>`
					+ `<div class="hlqr-sheet-actions"><button type="button" class="hlqr-sheet-button" data-hlqr-close>Close</button></div>`;
			}
		}

		this.close();
		const tmpBackdrop = document.createElement('div');
		tmpBackdrop.id = SHEET_ID;
		tmpBackdrop.className = 'hlqr-sheet-backdrop';
		tmpBackdrop.innerHTML = `<div class="hlqr-sheet" role="dialog" aria-modal="true">${tmpBody}</div>`;
		tmpBackdrop.addEventListener('click', (pEvent) =>
		{
			const tmpTarget = /** @type {HTMLElement} */ (pEvent.target);
			if (tmpTarget === tmpBackdrop || tmpTarget.hasAttribute('data-hlqr-close'))
			{
				this.close();
				return;
			}
			const tmpIndex = tmpTarget.getAttribute('data-hlqr-destination');
			if (tmpIndex !== null)
			{
				this.go(this.currentDestinations[Number(tmpIndex)]);
			}
		});
		document.body.appendChild(tmpBackdrop);
		const tmpFirstButton = tmpBackdrop.querySelector('button');
		if (tmpFirstButton)
		{
			tmpFirstButton.focus();
		}
	}

	/**
	 * @param {{ Label: string, URL?: string, onSelect?: () => void }} pDestination
	 */
	go(pDestination)
	{
		if (!pDestination)
		{
			return;
		}
		this.close();
		if (typeof pDestination.onSelect === 'function')
		{
			pDestination.onSelect();
			return;
		}
		if (pDestination.URL)
		{
			window.location.assign(pDestination.URL);
		}
	}

	close()
	{
		const tmpExisting = (typeof document !== 'undefined') ? document.getElementById(SHEET_ID) : null;
		if (tmpExisting)
		{
			tmpExisting.remove();
		}
	}
}

module.exports = HeadlightQRResultSheet;
module.exports.default_configuration = _DefaultConfiguration;
